"use client";

import { useState } from "react";
import {
  LayoutDashboard, Settings, CreditCard, Package, Users, Headphones,
  ChevronRight, ChevronDown, Check, AlertTriangle, Shield, Globe,
  Zap, Database, BarChart3, FileText, Calendar, Radio, Truck,
  HeartPulse, ShieldCheck, Briefcase, Bell, LogOut, Building2,
  ArrowUpRight, TrendingUp, Clock, CheckCircle2, XCircle,
  Cpu, Server, Lock, Star, Flame, Compass, Eye, LockKeyhole,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_ORG = {
  name: "Ockham Rescue Ltd",
  subdomain: "ockham",
  plan: "Enterprise",
  planColor: "text-purple-400",
  planBg: "bg-purple-500/10 border-purple-500/30",
  status: "active",
  billingEmail: "billing@ockhamrescue.co.uk",
  adminEmail: "j.heritage@ockhamrescue.co.uk",
  renewalDate: "1 Jun 2027",
  staffCount: 23,
  maxStaff: 999,
  storageUsed: 4.2,
  storageMax: 100,
  logo: "/ockhamlogo.jpeg",
};

const MOCK_INVOICES = [
  { ref: "INV-2026-0005", date: "1 May 2026", amount: "£499.00", status: "paid" },
  { ref: "INV-2026-0004", date: "1 Apr 2026", amount: "£499.00", status: "paid" },
  { ref: "INV-2026-0003", date: "1 Mar 2026", amount: "£499.00", status: "paid" },
  { ref: "INV-2026-0002", date: "1 Feb 2026", amount: "£399.00", status: "paid" },
  { ref: "INV-2026-0001", date: "1 Jan 2026", amount: "£399.00", status: "paid" },
];

const ALL_MODULES = [
  {
    id: "command_control",
    icon: Radio,
    label: "Command & Control",
    desc: "CAD dispatch, crew status board, MDT, major incident management",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20",
    enabled: true,
    includedIn: "Essential",
  },
  {
    id: "clinical",
    icon: HeartPulse,
    label: "Clinical Operations",
    desc: "Digital PRFs, NEWS2 scoring, offline capability, clinical audit",
    color: "text-red-400",
    bg: "bg-red-500/10 border-red-500/20",
    enabled: true,
    includedIn: "Essential",
  },
  {
    id: "workforce",
    icon: Users,
    label: "Workforce Management",
    desc: "Shift rostering, HR records, training hub, payroll, appraisals",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20",
    enabled: true,
    includedIn: "Professional",
  },
  {
    id: "governance",
    icon: ShieldCheck,
    label: "Governance & Compliance",
    desc: "CQC workflows, risk register, incident log, audit trails",
    color: "text-purple-400",
    bg: "bg-purple-500/10 border-purple-500/20",
    enabled: true,
    includedIn: "Professional",
  },
  {
    id: "fleet",
    icon: Truck,
    label: "Fleet & Assets",
    desc: "Vehicle inspections, defects, servicing, medical gas tracking",
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/20",
    enabled: true,
    includedIn: "Professional",
  },
  {
    id: "intelligence",
    icon: BarChart3,
    label: "Operational Intelligence",
    desc: "KPI dashboards, readiness scoring, compliance analytics, reports",
    color: "text-cyan-400",
    bg: "bg-cyan-500/10 border-cyan-500/20",
    enabled: true,
    includedIn: "Enterprise",
  },
  {
    id: "documentation",
    icon: FileText,
    label: "Documentation Hub",
    desc: "Policy register, PGDs, SOPs, version control, read receipts",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10 border-indigo-500/20",
    enabled: true,
    includedIn: "Professional",
  },
  {
    id: "events",
    icon: Calendar,
    label: "Event Planning",
    desc: "Event management, client portal, resource planning, reporting",
    color: "text-violet-400",
    bg: "bg-violet-500/10 border-violet-500/20",
    enabled: true,
    includedIn: "Professional",
  },
  {
    id: "fire",
    icon: Flame,
    label: "Fire & Rescue",
    desc: "Appliance management, mobilisation, resource tracking, fire control",
    color: "text-orange-400",
    bg: "bg-orange-500/10 border-orange-500/20",
    enabled: true,
    includedIn: "Enterprise",
  },
  {
    id: "sar",
    icon: Compass,
    label: "Search & Rescue",
    desc: "Search planning, GIS mapping, sector management, SAR equipment",
    color: "text-teal-400",
    bg: "bg-teal-500/10 border-teal-500/20",
    enabled: false,
    includedIn: "Enterprise",
  },
  {
    id: "security",
    icon: Eye,
    label: "Security Operations",
    desc: "Patrol tracker, security incidents, access control, missing persons",
    color: "text-yellow-400",
    bg: "bg-yellow-500/10 border-yellow-500/20",
    enabled: false,
    includedIn: "Enterprise",
  },
];

const NAV_ITEMS = [
  { id: "overview",      label: "Account Overview",    icon: LayoutDashboard },
  { id: "modules",       label: "Modules & Features",  icon: Package },
  { id: "billing",       label: "Billing & Invoices",  icon: CreditCard },
  { id: "organisation",  label: "Organisation",        icon: Building2 },
  { id: "team",          label: "Account Admins",      icon: Users },
  { id: "support",       label: "Support & Roadmap",   icon: Headphones },
  { id: "settings",      label: "Settings",            icon: Settings },
];

// ─── Sub-panels ───────────────────────────────────────────────────────────────

function OverviewPanel() {
  return (
    <div className="space-y-8">
      {/* Plan summary */}
      <div className="rounded-2xl border border-purple-500/25 bg-purple-500/5 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-purple-400">Enterprise Plan</span>
            </div>
            <h2 className="text-2xl font-extrabold text-white">{MOCK_ORG.name}</h2>
            <p className="text-sm text-slate-400 mt-1">{MOCK_ORG.subdomain}.opscom.co.uk</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold text-white">£499<span className="text-base font-medium text-slate-400">/month</span></p>
            <p className="text-xs text-slate-500 mt-1">Renews {MOCK_ORG.renewalDate}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Staff Accounts", value: `${MOCK_ORG.staffCount} / ∞`, sub: "Unlimited on Enterprise" },
            { label: "Active Modules", value: "9 / 11", sub: "2 available to enable" },
            { label: "Storage Used", value: `${MOCK_ORG.storageUsed} GB`, sub: `of ${MOCK_ORG.storageMax} GB` },
            { label: "Plan Status", value: "Active", sub: "No issues", valueColor: "text-emerald-400" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-900/60 border border-white/8 p-4">
              <p className="text-xs text-slate-500 mb-1">{s.label}</p>
              <p className={cn("text-xl font-extrabold", s.valueColor ?? "text-white")}>{s.value}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Account Activity</h3>
          <span className="text-[10px] text-slate-500">Last 30 days</span>
        </div>
        <div className="divide-y divide-white/5">
          {[
            { icon: CheckCircle2, color: "text-emerald-400", label: "Invoice INV-2026-0005 paid", time: "1 May 2026", sub: "£499.00 — Auto-payment" },
            { icon: Package, color: "text-blue-400", label: "Fire & Rescue module enabled", time: "28 Apr 2026", sub: "Added to Enterprise plan" },
            { icon: Users, color: "text-violet-400", label: "3 new staff accounts created", time: "22 Apr 2026", sub: "By j.heritage@ockhamrescue.co.uk" },
            { icon: ShieldCheck, color: "text-purple-400", label: "CQC compliance review completed", time: "15 Apr 2026", sub: "Readiness score: 87%" },
            { icon: CheckCircle2, color: "text-emerald-400", label: "Invoice INV-2026-0004 paid", time: "1 Apr 2026", sub: "£499.00 — Auto-payment" },
          ].map((a, i) => (
            <div key={i} className="flex items-start gap-4 px-6 py-4">
              <a.icon className={cn("w-4 h-4 mt-0.5 shrink-0", a.color)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200">{a.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{a.sub}</p>
              </div>
              <span className="text-[11px] text-slate-600 shrink-0">{a.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { icon: CreditCard, label: "View Billing", sub: "Invoices & payment methods", color: "text-blue-400" },
          { icon: Package, label: "Manage Modules", sub: "Enable or disable platform modules", color: "text-purple-400" },
          { icon: Headphones, label: "Contact Support", sub: "Raise a support ticket", color: "text-emerald-400" },
        ].map((q) => (
          <div key={q.label} className="rounded-xl border border-white/8 bg-slate-900/40 p-5 flex items-center gap-4 hover:bg-slate-800/40 transition-colors cursor-pointer group">
            <div className={cn("w-10 h-10 rounded-xl bg-slate-800 border border-white/8 flex items-center justify-center", q.color)}>
              <q.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{q.label}</p>
              <p className="text-xs text-slate-500">{q.sub}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 ml-auto group-hover:text-slate-400 transition-colors" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ModulesPanel() {
  const [modules, setModules] = useState(ALL_MODULES);

  function toggleModule(id: string) {
    setModules((prev) => prev.map((m) => m.id === id ? { ...m, enabled: !m.enabled } : m));
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6">
        <h3 className="text-base font-bold text-white mb-1">Platform Modules</h3>
        <p className="text-sm text-slate-400">All Enterprise plan modules are included. Enable or disable modules for your workspace. Billing updates on the next cycle for add-on modules.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {modules.map((mod) => {
          const isLocked = mod.includedIn === "Essential";
          return (
            <div key={mod.id} className={cn("rounded-2xl border p-5 transition-all", mod.enabled ? "border-white/12 bg-slate-900/60" : "border-white/5 bg-slate-900/20 opacity-60")}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border", mod.bg)}>
                    <mod.icon className={cn("w-5 h-5", mod.color)} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{mod.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{mod.desc}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] font-semibold text-slate-500 border border-white/10 rounded px-1.5 py-0.5">{mod.includedIn}</span>
                      {isLocked && (
                        <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                          <LockKeyhole className="w-2.5 h-2.5" /> Required
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isLocked ? (
                  <div className="shrink-0 w-11 h-6 rounded-full relative overflow-hidden bg-blue-600/50 cursor-not-allowed" title="Essential module — cannot be disabled">
                    <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white/60 shadow translate-x-[22px]" />
                  </div>
                ) : (
                  <button
                    onClick={() => toggleModule(mod.id)}
                    className={cn(
                      "shrink-0 w-11 h-6 rounded-full relative overflow-hidden transition-colors",
                      mod.enabled ? "bg-blue-600" : "bg-slate-700",
                    )}
                  >
                    <span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200", mod.enabled ? "translate-x-[22px]" : "translate-x-0.5")} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-200/80">
          Disabling a module removes it from your workspace navigation but <strong>does not delete data</strong>. All records are retained and restored when the module is re-enabled. Changes take effect immediately.
        </p>
      </div>
    </div>
  );
}

function BillingPanel() {
  return (
    <div className="space-y-6">
      {/* Current plan */}
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white">Current Plan</h3>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">Enterprise</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { label: "Monthly Cost", value: "£499.00" },
            { label: "Billing Cycle", value: "Monthly" },
            { label: "Next Renewal", value: MOCK_ORG.renewalDate },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-800/60 border border-white/8 p-4">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className="text-lg font-extrabold text-white mt-1">{s.value}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-3">
          <button className="text-sm text-blue-400 hover:text-blue-300 border border-blue-500/30 hover:border-blue-500/60 px-4 py-2 rounded-lg transition-colors">
            Upgrade Plan
          </button>
          <button className="text-sm text-slate-400 hover:text-white border border-white/10 hover:border-white/20 px-4 py-2 rounded-lg transition-colors">
            Switch to Annual (Save 15%)
          </button>
        </div>
      </div>

      {/* Payment method */}
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6">
        <h3 className="text-base font-bold text-white mb-4">Payment Method</h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-8 rounded-md bg-slate-700 border border-white/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Visa ending 4242</p>
              <p className="text-xs text-slate-500">Expires 09/2028</p>
            </div>
          </div>
          <button className="text-xs text-blue-400 hover:text-blue-300 transition-colors">Update</button>
        </div>
      </div>

      {/* Invoices */}
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/8">
          <h3 className="text-base font-bold text-white">Invoice History</h3>
        </div>
        <div className="divide-y divide-white/5">
          {MOCK_INVOICES.map((inv) => (
            <div key={inv.ref} className="flex items-center gap-4 px-6 py-4">
              <FileText className="w-4 h-4 text-slate-500 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{inv.ref}</p>
                <p className="text-xs text-slate-500">{inv.date}</p>
              </div>
              <span className="text-sm font-bold text-white">{inv.amount}</span>
              <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full", inv.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/15 text-amber-400")}>
                {inv.status.toUpperCase()}
              </span>
              <button className="text-xs text-slate-500 hover:text-blue-400 transition-colors">PDF</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrganisationPanel() {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6 space-y-5">
        <h3 className="text-base font-bold text-white">Organisation Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { label: "Organisation Name", value: MOCK_ORG.name },
            { label: "Subdomain", value: `${MOCK_ORG.subdomain}.opscom.co.uk` },
            { label: "Admin Email", value: MOCK_ORG.adminEmail },
            { label: "Billing Email", value: MOCK_ORG.billingEmail },
          ].map((f) => (
            <div key={f.label}>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">{f.label}</label>
              <div className="w-full rounded-xl border border-white/10 bg-slate-800/60 px-4 py-3 text-sm text-slate-200">
                {f.value}
              </div>
            </div>
          ))}
        </div>
        <button className="text-sm bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl font-semibold transition-colors">
          Save Changes
        </button>
      </div>

      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6 space-y-4">
        <h3 className="text-base font-bold text-white">Branding</h3>
        <p className="text-sm text-slate-400">Upload your organisation logo and configure your workspace theme. Your branding appears in staff-facing interfaces, reports, and client communications.</p>
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-slate-800 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
            <img src={MOCK_ORG.logo} alt="Logo" className="w-full h-full object-cover rounded-2xl" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          </div>
          <div>
            <button className="text-sm border border-white/15 hover:border-white/30 bg-white/5 text-white px-4 py-2 rounded-lg transition-colors">Upload Logo</button>
            <p className="text-xs text-slate-500 mt-1.5">PNG or SVG, max 2MB, min 200×200px</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6 space-y-4">
        <h3 className="text-base font-bold text-white">Custom Domain</h3>
        <p className="text-sm text-slate-400">Configure a custom domain for your operational workspace — e.g. <span className="text-slate-300 font-mono">ops.yourorganisation.co.uk</span>. Available on Enterprise plan.</p>
        <div className="flex gap-3">
          <input type="text" placeholder="ops.yourorganisation.co.uk" className="flex-1 rounded-xl border border-white/10 bg-slate-800/60 px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/50" />
          <button className="text-sm bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl font-semibold transition-colors shrink-0">
            Configure
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamPanel() {
  const admins = [
    { name: "James Heritage", email: "j.heritage@ockhamrescue.co.uk", role: "Owner", initials: "JH", color: "from-purple-500 to-purple-700" },
    { name: "Sarah Mitchell", email: "s.mitchell@ockhamrescue.co.uk", role: "Admin", initials: "SM", color: "from-blue-500 to-blue-700" },
  ];
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
          <h3 className="text-base font-bold text-white">Account Administrators</h3>
          <button className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg font-semibold transition-colors">
            Invite Admin
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {admins.map((a) => (
            <div key={a.email} className="flex items-center gap-4 px-6 py-4">
              <div className={cn("w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center text-xs font-bold text-white shrink-0", a.color)}>
                {a.initials}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{a.name}</p>
                <p className="text-xs text-slate-500">{a.email}</p>
              </div>
              <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full border", a.role === "Owner" ? "bg-purple-500/15 text-purple-300 border-purple-500/30" : "bg-blue-500/15 text-blue-300 border-blue-500/30")}>
                {a.role}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-white/8 bg-slate-900/30 p-5">
        <p className="text-xs text-slate-400">Account administrators can manage billing, modules, organisation settings, and invite new staff. They do not automatically have access to the operational workspace unless also granted a staff account with appropriate permissions.</p>
      </div>
    </div>
  );
}

function SupportPanel() {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { icon: Headphones, title: "Raise a Support Ticket", desc: "For operational, technical, or account issues. Enterprise SLA: response within 4 hours.", color: "text-blue-400", cta: "Open Ticket" },
          { icon: BarChart3, title: "Platform Status", desc: "Live status page for all OpsCom services. Uptime, incidents, and maintenance windows.", color: "text-emerald-400", cta: "View Status" },
          { icon: FileText, title: "Documentation & Guides", desc: "Platform user guides, setup documentation, API reference, and onboarding walkthroughs.", color: "text-violet-400", cta: "View Docs" },
          { icon: Star, title: "Feature Requests & Roadmap", desc: "Vote on upcoming features, submit requests, and track roadmap items relevant to your sector.", color: "text-amber-400", cta: "View Roadmap" },
        ].map((s) => (
          <div key={s.title} className="rounded-2xl border border-white/8 bg-slate-900/40 p-6 flex flex-col gap-4">
            <s.icon className={cn("w-6 h-6", s.color)} />
            <div>
              <h3 className="text-sm font-bold text-white mb-1">{s.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
            <button className="mt-auto self-start text-xs border border-white/12 hover:border-white/25 text-slate-300 hover:text-white px-4 py-2 rounded-lg transition-colors">
              {s.cta}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/8 bg-slate-900/40 p-6">
        <h3 className="text-base font-bold text-white mb-4">Your Account Manager</h3>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">OM</div>
          <div>
            <p className="text-sm font-semibold text-white">OpsCom Enterprise Team</p>
            <p className="text-xs text-slate-400">support@opscom.co.uk</p>
            <p className="text-xs text-slate-500 mt-0.5">Dedicated support for Enterprise customers · Mon–Fri 08:00–20:00</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/8 bg-slate-900/40 divide-y divide-white/5">
        {[
          { label: "Two-Factor Authentication", desc: "Require 2FA for all account administrators", enabled: true },
          { label: "SSO Integration", desc: "Single Sign-On via Microsoft 365 or Google Workspace", enabled: false },
          { label: "Billing Notifications", desc: "Email alerts for invoices, renewals, and payment failures", enabled: true },
          { label: "Platform Release Notes", desc: "Email digest of new features and platform updates", enabled: true },
          { label: "Security Alert Emails", desc: "Alerts for unusual account activity or failed login attempts", enabled: true },
        ].map((s) => (
          <div key={s.label} className="flex items-center justify-between px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-white">{s.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
            </div>
            <div className={cn("w-11 h-6 rounded-full relative overflow-hidden cursor-pointer transition-colors", s.enabled ? "bg-blue-600" : "bg-slate-700")}>
              <span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200", s.enabled ? "translate-x-[22px]" : "translate-x-0.5")} />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6">
        <h3 className="text-sm font-bold text-red-300 mb-2">Danger Zone</h3>
        <p className="text-xs text-slate-400 mb-4">These actions are irreversible. They affect your entire organisation and all operational data.</p>
        <div className="flex gap-3 flex-wrap">
          <button className="text-xs text-red-400 border border-red-500/30 hover:border-red-500/60 px-4 py-2 rounded-lg transition-colors">
            Export All Data
          </button>
          <button className="text-xs text-red-400 border border-red-500/30 hover:border-red-500/60 px-4 py-2 rounded-lg transition-colors">
            Cancel Subscription
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AccountPortalPage() {
  const [activePanel, setActivePanel] = useState<string>("overview");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const currentItem = NAV_ITEMS.find((n) => n.id === activePanel) ?? NAV_ITEMS[0];

  const panelComponents: Record<string, React.ReactNode> = {
    overview: <OverviewPanel />,
    modules: <ModulesPanel />,
    billing: <BillingPanel />,
    organisation: <OrganisationPanel />,
    team: <TeamPanel />,
    support: <SupportPanel />,
    settings: <SettingsPanel />,
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">

      {/* ── Top bar ── */}
      <header className="sticky top-0 z-30 border-b border-white/8 bg-slate-950/95 backdrop-blur-md h-14 flex items-center px-4 gap-4">
        <img src="/opscom.png" alt="OpsCom" className="h-7 w-auto" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 border-l border-white/10 pl-4">Customer Portal</span>
        <div className="ml-auto flex items-center gap-3">
          <button className="relative text-slate-400 hover:text-white transition-colors">
            <Bell className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-xs font-bold text-white">JH</div>
        </div>
      </header>

      <div className="flex flex-1">

        {/* ── Sidebar ── */}
        <aside className="w-64 shrink-0 border-r border-white/8 bg-slate-900/50 hidden md:flex flex-col">
          {/* Org card */}
          <div className="p-4 border-b border-white/8">
            <div className="rounded-xl border border-white/8 bg-slate-900 px-3 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-700 flex items-center justify-center text-xs font-bold text-white overflow-hidden shrink-0">
                <img src={MOCK_ORG.logo} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{MOCK_ORG.name}</p>
                <p className="text-[10px] text-purple-400 font-semibold">Enterprise Plan</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 space-y-0.5">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => setActivePanel(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left",
                  activePanel === item.id
                    ? "bg-blue-600/20 text-blue-300 border border-blue-500/25"
                    : "text-slate-400 hover:text-white hover:bg-white/5",
                )}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* Bottom */}
          <div className="p-3 border-t border-white/8 space-y-0.5">
            <a
              href="https://ockham.opscom.co.uk"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-all"
            >
              <ArrowUpRight className="w-4 h-4 shrink-0" />
              Open Workspace
            </a>
            <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/5 transition-all">
              <LogOut className="w-4 h-4 shrink-0" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0 p-6 md:p-8">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-white">{currentItem.label}</h1>
            <p className="text-sm text-slate-400 mt-1">
              {activePanel === "overview" && "Account summary, usage, and recent activity for your OpsCom subscription."}
              {activePanel === "modules" && "Enable or disable platform modules. Changes take effect immediately."}
              {activePanel === "billing" && "Manage your subscription, payment methods, and download invoices."}
              {activePanel === "organisation" && "Organisation details, branding, and domain configuration."}
              {activePanel === "team" && "Manage account administrators for your OpsCom account."}
              {activePanel === "support" && "Support tickets, documentation, feature requests, and your account manager."}
              {activePanel === "settings" && "Security settings, notification preferences, and account configuration."}
            </p>
          </div>

          {panelComponents[activePanel]}
        </main>
      </div>
    </div>
  );
}
